import { useCallback, useContext } from "react";
import {
  AccountData,
  Event,
  TicketV2,
  NotificationType,
  RequestStatus,
} from "@markit/common.types";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { MixpanelContext } from "../../context/AnalyticsService";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { useDispatch } from "react-redux";
import { isEventRelatedType } from "@markit/common.utils";

type ProfileTicketItemButtonProps = {
  ticketItem: TicketV2;
  profileItem: AccountData | undefined;
  organizer?: boolean;
  scanTicket: (eventId: string, ticketId: string) => void;
  unscanTicket: (eventId: string, ticketId: string) => void;
  approveRequestGuest: (
    eventId: string,
    ticketId: string,
    requester: AccountData
  ) => void;
  denyRequestGuest: (
    eventId: string,
    ticketId: string,
    requester: AccountData
  ) => void;
  liveEvent: Event;
  loadingTicketAction: boolean;
};

export const ProfileTicketItemButton = ({
  liveEvent,
  ticketItem,
  profileItem,
  organizer,
  scanTicket,
  unscanTicket,
  approveRequestGuest,
  denyRequestGuest,
  loadingTicketAction,
}: ProfileTicketItemButtonProps) => {
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();

  const styles = {
    approveButton: {
      paddingBlock: 7,
      width: 79,
      borderRadius: 8,
      backgroundColor: Colors.BLACK,
    },

    approveText: {
      fontSize: 12,
      color: Colors.WHITE,
      fontWeight: "600",
    },

    checkedInButton: {
      paddingBlock: 7,
      width: 79,
      borderRadius: 8,
      backgroundColor: Colors.GRAY6,
    },

    checkedInText: {
      fontSize: 11,
      color: Colors.GRAY1,
      fontWeight: "600",
    },

    checkInButton: {
      paddingBlock: 7,
      width: 79,
      borderRadius: 8,
      backgroundColor: Colors.BLACK,
    },

    checkInText: {
      fontSize: 12,
      color: Colors.WHITE,
      fontWeight: "600",
    },

    denyButton: {
      borderRadius: 8,
      backgroundColor: Colors.GRAY6,
      marginLeft: 7,
      width: 28,
    },

    shareRoleButton: {
      paddingBlock: 7,
      width: 106,
      borderRadius: 8,
      backgroundColor: Colors.BLACK,
    },
  };

  const sendUnassignedRoleLink = useCallback(async () => {
    try {
      mixpanel.track("Pressed Share Role For Unassigned Organizer Role", {
        event_id: liveEvent.id,
      });
      const el = document.createElement("input");
      el.value =
        "You have been invited to be a " +
        ticketItem.role.type +
        " for an event! " +
        `\nhttps://markitai.com/e/${liveEvent.id}?ticketId=${ticketItem.id}`;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);

      showNotificationBanner(dispatch, "Copied!", NotificationType.AFFIRMATIVE);
    } catch (error: any) {
      alert("Issue trying to send organizer role link");
      console.error(error.message);
    }
  }, [dispatch, liveEvent.id, mixpanel, ticketItem.id, ticketItem.role.type]);

  return (
    <>
      <div style={{ marginLeft: 10, flexDirection: "row" }}>
        {ticketItem.requestStatus === RequestStatus.PENDING && profileItem ? (
          <div className="RowNormal">
            <div
              className="Centering"
              style={{
                ...styles.approveButton,
                pointerEvents: loadingTicketAction ? "none" : "all",
                cursor: "pointer",
              }}
              onClick={() =>
                approveRequestGuest(liveEvent.id, ticketItem.id, profileItem)
              }
            >
              <span style={styles.approveText}>Approve</span>
            </div>
            <div
              className="Centering"
              style={{
                ...styles.denyButton,
                pointerEvents: loadingTicketAction ? "none" : "all",
                cursor: "pointer",
              }}
              onClick={() =>
                denyRequestGuest(liveEvent.id, ticketItem.id, profileItem)
              }
            >
              <Icon
                icon="ion:close"
                height={20}
                style={{ color: Colors.RED3 }}
              />
            </div>
          </div>
        ) : ticketItem.requestStatus ===
          RequestStatus.REFUNDED ? null : isEventRelatedType(
            liveEvent.eventType
          ) ? (
          organizer && !profileItem ? (
            <div
              className="Centering AlignedRow"
              style={{ ...styles.shareRoleButton, cursor: "pointer" }}
              onClick={sendUnassignedRoleLink}
            >
              <Icon
                icon="material-symbols:link"
                height={15}
                style={{ color: Colors.WHITE, marginRight: 7 }}
              />
              <span style={styles.checkInText}>Share Role</span>
            </div>
          ) : profileItem ? (
            <>
              {ticketItem.scanned ? (
                <div
                  className="CenteringSelect"
                  style={{ ...styles.checkedInButton }}
                  onClick={() => unscanTicket(liveEvent.id, ticketItem.id)}
                >
                  <span style={styles.checkedInText}>Checked in</span>
                </div>
              ) : (
                <div
                  className="CenteringSelect"
                  style={{ ...styles.checkInButton }}
                  onClick={() => {
                    scanTicket(liveEvent.id, ticketItem.id);
                  }}
                >
                  <span style={styles.checkInText}>Check In</span>
                </div>
              )}
            </>
          ) : null
        ) : null}
      </div>
    </>
  );
};
